import React, { useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";

import schoolLogo from "./RoyalLogo.png";
import defaultAvatar from "./studentava.jpg";

/* ============================================================
   ROYAL PRIESTHOOD ACADEMY — COLOR SYSTEM
============================================================ */

const COLORS = {
  royalBlue: "#1746A2",
  royalBlueDark: "#103579",
  skyBlue: "#49A9E8",
  skyBlueLight: "#EAF6FF",
  paleBlue: "#F5FAFF",
  headerBlue: "#E5F1FC",
  borderBlue: "#C9DDF1",

  black: "#172033",
  charcoal: "#364152",
  gray: "#64748B",
  grayLight: "#94A3B8",
  line: "#DCE5EF",
  white: "#FFFFFF",

  green: "#16834A",
  greenLight: "#E9F8EF",
  greenBorder: "#A9DFC0",

  red: "#C62828",
};

/* ============================================================
   A4 LANDSCAPE — SINGLE-PAGE STYLES
============================================================ */

const styles = StyleSheet.create({
  page: {
    backgroundColor: COLORS.paleBlue,
    paddingTop: 10,
    paddingBottom: 8,
    paddingHorizontal: 17,
    fontFamily: "Helvetica",
    fontSize: 7,
    color: COLORS.black,
  },

  /* HEADER */

  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderBottomWidth: 3,
    borderBottomColor: COLORS.royalBlue,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 5,
  },

  schoolSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 43,
    height: 43,
    objectFit: "contain",
    borderRadius: 22,
    borderWidth: 1.5,
    borderColor: COLORS.skyBlue,
    marginRight: 9,
  },

  schoolDetails: {
    flex: 1,
  },

  schoolName: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.royalBlueDark,
    marginBottom: 2,
  },

  schoolMotto: {
    fontSize: 7,
    color: COLORS.royalBlue,
    marginBottom: 2,
  },

  schoolAddress: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginBottom: 1,
  },

  secureBadge: {
    backgroundColor: COLORS.skyBlueLight,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 9,
    alignItems: "center",
    marginLeft: 8,
  },

  secureBadgeText: {
    color: COLORS.royalBlue,
    fontSize: 8,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },

  secureBadgeSubtext: {
    color: COLORS.gray,
    fontSize: 5.5,
    marginTop: 2,
  },

  /* TITLE */

  titleBox: {
    backgroundColor: COLORS.headerBlue,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.skyBlue,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 7,
    marginBottom: 6,
  },

  title: {
    textAlign: "center",
    color: COLORS.royalBlueDark,
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },

  subtitle: {
    textAlign: "center",
    color: COLORS.charcoal,
    fontSize: 6.5,
    marginTop: 2,
    lineHeight: 1.2,
  },

  /* MAIN LAYOUT */

  mainContent: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  leftColumn: {
    width: 145,
    marginRight: 7,
  },

  rightColumn: {
    flex: 1,
  },

  /* PROFILE */

  profileCard: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 6,
    padding: 7,
    alignItems: "center",
    marginBottom: 6,
  },

  profileAccent: {
    width: "100%",
    height: 3,
    backgroundColor: COLORS.skyBlue,
    borderRadius: 2,
    marginBottom: 6,
  },

  avatar: {
    width: 65,
    height: 65,
    objectFit: "cover",
    borderRadius: 33,
    borderWidth: 2,
    borderColor: COLORS.skyBlue,
    marginBottom: 5,
  },

  studentName: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.black,
    textAlign: "center",
    lineHeight: 1.2,
  },

  studentID: {
    marginTop: 3,
    color: COLORS.royalBlue,
    fontSize: 6.8,
    fontWeight: "bold",
    textAlign: "center",
  },

  verifiedBadge: {
    marginTop: 5,
    backgroundColor: COLORS.greenLight,
    borderWidth: 1,
    borderColor: COLORS.greenBorder,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 6,
  },

  verifiedText: {
    color: COLORS.green,
    fontWeight: "bold",
    fontSize: 6.5,
  },

  profileNote: {
    marginTop: 5,
    color: COLORS.gray,
    fontSize: 6,
    textAlign: "center",
    lineHeight: 1.2,
  },

  /* ACCOUNT SUMMARY */

  credentialSummary: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 5,
    padding: 6,
  },

  summaryTitle: {
    color: COLORS.royalBlue,
    fontWeight: "bold",
    fontSize: 7,
    marginBottom: 4,
  },

  summaryLabel: {
    color: COLORS.gray,
    fontSize: 6,
    marginBottom: 2,
  },

  summaryValue: {
    color: COLORS.black,
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 4,
  },

  summaryStatus: {
    color: COLORS.green,
    fontSize: 7,
    fontWeight: "bold",
  },

  /* SECTIONS */

  section: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 5,
    overflow: "hidden",
    marginBottom: 6,
  },

  sectionHeader: {
    backgroundColor: COLORS.headerBlue,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderBlue,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royalBlue,
    paddingVertical: 4,
    paddingHorizontal: 7,
  },

  sectionHeaderText: {
    color: COLORS.royalBlueDark,
    fontWeight: "bold",
    fontSize: 7.5,
    letterSpacing: 0.2,
  },

  sectionBody: {
    paddingVertical: 2,
    paddingHorizontal: 7,
  },

  /* DATA ROWS */

  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 3,
    borderBottomWidth: 0.4,
    borderBottomColor: COLORS.line,
  },

  label: {
    width: 91,
    paddingRight: 5,
    color: COLORS.gray,
    fontSize: 6.5,
    fontWeight: "bold",
  },

  value: {
    flex: 1,
    color: COLORS.black,
    fontSize: 7,
    lineHeight: 1.15,
  },

  username: {
    flex: 1,
    color: COLORS.royalBlue,
    fontSize: 7,
    fontWeight: "bold",
  },

  password: {
    flex: 1,
    color: COLORS.red,
    fontSize: 7,
    fontWeight: "bold",
  },

  statusActive: {
    flex: 1,
    color: COLORS.green,
    fontSize: 7,
    fontWeight: "bold",
  },

  informationColumns: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  informationColumn: {
    flex: 1,
  },

  informationColumnLeft: {
    marginRight: 7,
  },

  /* SECURITY INSTRUCTIONS */

  credentialBox: {
    marginTop: 4,
    backgroundColor: COLORS.skyBlueLight,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 4,
    padding: 5,
  },

  credentialTitle: {
    color: COLORS.royalBlueDark,
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 3,
  },

  warningText: {
    fontSize: 6,
    color: COLORS.charcoal,
    lineHeight: 1.2,
    marginBottom: 1.5,
  },

  securityBox: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 7,
    marginBottom: 5,
  },

  securityTitle: {
    color: COLORS.royalBlue,
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 3,
  },

  securityItem: {
    fontSize: 6,
    color: COLORS.charcoal,
    lineHeight: 1.2,
    marginBottom: 1,
  },

  /* AUTHENTICATION AREA */

  authenticationArea: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    borderRadius: 5,
    paddingVertical: 4,
    paddingHorizontal: 7,
    marginBottom: 5,
  },

  stampBox: {
    width: 68,
    height: 37,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.grayLight,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },

  stampText: {
    fontSize: 6,
    color: COLORS.gray,
    textAlign: "center",
    lineHeight: 1.3,
  },

  qrBox: {
    width: 68,
    height: 37,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.grayLight,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },

  signatureArea: {
    flex: 1,
    marginLeft: 12,
    marginRight: 4,
  },

  signatureTitle: {
    color: COLORS.black,
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 9,
  },

  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray,
    width: 140,
    marginBottom: 3,
  },

  signatureCaption: {
    fontSize: 6,
    color: COLORS.gray,
  },

  /* FOOTER */

  footer: {
    borderTopWidth: 1,
    borderTopColor: COLORS.borderBlue,
    paddingTop: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    flex: 1,
    fontSize: 5.8,
    color: COLORS.gray,
    lineHeight: 1.2,
  },

  footerRight: {
    fontSize: 5.8,
    color: COLORS.royalBlue,
    fontWeight: "bold",
    textAlign: "right",
    marginLeft: 8,
  },
});

/* ============================================================
   REUSABLE STUDENT INFORMATION ROW
============================================================ */

const InformationRow = ({ label, value }) => (
  <View style={styles.row}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || "N/A"}</Text>
  </View>
);

/* ============================================================
   STUDENT AUTHENTICATION PDF
   ONE A4 LANDSCAPE PAGE
============================================================ */

const StudentAuthenticationPDF = ({ student }) => {
  const profileImage =
    student?.profile_image ||
    student?.student_admission_image ||
    defaultAvatar;

  const studentInformation = [
    ["Full Name", student?.full_name],
    ["Student ID", student?.admission_id],
    ["Gender", student?.gender],
    ["Class", student?.grade_class_name],
    ["Academic Year", student?.current_academic_year],
    ["Date of Birth", student?.date_of_birth],
    ["Parent / Guardian", student?.mother_name],
    ["Guardian Phone", student?.parent_contact],
  ];

  const portalEmail =
    student?.email || student?.admission_id || "N/A";

  const initialPassword =
    student?.initial_password || student?.password || "N/A";

  return (
    <Document
      title={`RPHA Student Authentication - ${
        student?.full_name || "Student"
      }`}
      author="Royal Priesthood Academy"
      subject="Official Student Authentication Document"
      creator="Polu Integrated School Management System"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap={false}
      >
        {/* SCHOOL HEADER */}

        <View style={styles.header}>
          <View style={styles.schoolSection}>
            <Image src={schoolLogo} style={styles.logo} />

            <View style={styles.schoolDetails}>
              <Text style={styles.schoolName}>
                ROYAL PRIESTHOOD ACADEMY (RPHA)
              </Text>

              <Text style={styles.schoolMotto}>
                Learning Today • Leading Tomorrow
              </Text>

              <Text style={styles.schoolAddress}>
                Zazay Community, Paynesville City, Monrovia, Liberia
              </Text>

              <Text style={styles.schoolAddress}>
                Official Student Records • Identity & Portal Verification
              </Text>
            </View>
          </View>

          <View style={styles.secureBadge}>
            <Text style={styles.secureBadgeText}>SECURE RECORD</Text>
            <Text style={styles.secureBadgeSubtext}>
              OFFICIAL DOCUMENT
            </Text>
          </View>
        </View>

        {/* DOCUMENT TITLE */}

        <View style={styles.titleBox}>
          <Text style={styles.title}>
            STUDENT AUTHENTICATION DOCUMENT
          </Text>

          <Text style={styles.subtitle}>
            Official confirmation of student registration and authorized
            access to the Royal Priesthood Academy school management system.
          </Text>
        </View>

        {/* TWO-COLUMN CONTENT */}

        <View style={styles.mainContent}>
          {/* LEFT: PROFILE */}

          <View style={styles.leftColumn}>
            <View style={styles.profileCard}>
              <View style={styles.profileAccent} />

              <Image src={profileImage} style={styles.avatar} />

              <Text style={styles.studentName}>
                {student?.full_name || "N/A"}
              </Text>

              <Text style={styles.studentID}>
                STUDENT ID: {student?.admission_id || "N/A"}
              </Text>

              <View style={styles.verifiedBadge}>
                <Text style={styles.verifiedText}>
                  ✓ VERIFIED STUDENT
                </Text>
              </View>

              <Text style={styles.profileNote}>
                Verify the student's identity against official school records.
              </Text>
            </View>

            <View style={styles.credentialSummary}>
              <Text style={styles.summaryTitle}>
                PORTAL ACCOUNT SUMMARY
              </Text>

              <Text style={styles.summaryLabel}>
                LOGIN EMAIL / USERNAME
              </Text>

              <Text style={styles.summaryValue}>
                {portalEmail}
              </Text>

              <Text style={styles.summaryLabel}>
                ACCOUNT STATUS
              </Text>

              <Text style={styles.summaryStatus}>ACTIVE</Text>
            </View>
          </View>

          {/* RIGHT: STUDENT DATA AND PORTAL */}

          <View style={styles.rightColumn}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>
                  STUDENT INFORMATION
                </Text>
              </View>

              <View style={styles.sectionBody}>
                <View style={styles.informationColumns}>
                  <View
                    style={[
                      styles.informationColumn,
                      styles.informationColumnLeft,
                    ]}
                  >
                    {studentInformation.slice(0, 4).map(
                      ([label, value]) => (
                        <InformationRow
                          key={label}
                          label={label}
                          value={value}
                        />
                      )
                    )}
                  </View>

                  <View style={styles.informationColumn}>
                    {studentInformation.slice(4).map(
                      ([label, value]) => (
                        <InformationRow
                          key={label}
                          label={label}
                          value={value}
                        />
                      )
                    )}
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>
                  STUDENT PORTAL ACCESS & LOGIN CREDENTIALS
                </Text>
              </View>

              <View style={styles.sectionBody}>
                <View style={styles.row}>
                  <Text style={styles.label}>Email / Username</Text>
                  <Text style={styles.username}>{portalEmail}</Text>
                </View>

                <View style={styles.row}>
                  <Text style={styles.label}>Initial Password</Text>
                  <Text style={styles.password}>
                    {initialPassword}
                  </Text>
                </View>

                <View style={styles.row}>
                  <Text style={styles.label}>Account Status</Text>
                  <Text style={styles.statusActive}>ACTIVE</Text>
                </View>

                <View style={styles.credentialBox}>
                  <Text style={styles.credentialTitle}>
                    IMPORTANT SECURITY INFORMATION
                  </Text>

                  <Text style={styles.warningText}>
                    • Credentials are intended only for the registered student.
                    Never share your password with unauthorized persons.
                  </Text>

                  <Text style={styles.warningText}>
                    • Change the initial password after the first successful
                    login.
                  </Text>

                  <Text style={styles.warningText}>
                    • Contact school administration immediately if account
                    access is compromised.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* SECURITY NOTICE */}

        <View style={styles.securityBox}>
          <Text style={styles.securityTitle}>
            SECURITY & AUTHENTICITY NOTICE
          </Text>

          <Text style={styles.securityItem}>
            • Generated from student information supplied to the school's
            management system; unauthorized alteration may invalidate it.
          </Text>

          <Text style={styles.securityItem}>
            • The student's identity, photograph, and details must match
            official school records.
          </Text>

          <Text style={styles.securityItem}>
            • Final verification must be obtained from authorized school
            administration personnel.
          </Text>

          <Text style={styles.securityItem}>
            • Confidential student information: for the registered student,
            guardian, and authorized school personnel only.
          </Text>
        </View>

        {/* AUTHENTICATION AREA */}

        <View style={styles.authenticationArea}>
          <View style={styles.stampBox}>
            <Text style={styles.stampText}>
              SCHOOL{"\n"}OFFICIAL STAMP
            </Text>
          </View>

          <View style={styles.qrBox}>
            <Text style={styles.stampText}>
              QR CODE{"\n"}PLACEHOLDER
            </Text>
          </View>

          <View style={styles.signatureArea}>
            <Text style={styles.signatureTitle}>
              Authorized Signature
            </Text>

            <View style={styles.signatureLine} />

            <Text style={styles.signatureCaption}>
              Principal / Authorized Administrator
            </Text>
          </View>
        </View>

        {/* FOOTER */}

        <View style={styles.footer}>
          <Text style={styles.footerLeft}>
            © {new Date().getFullYear()} Royal Priesthood Academy (RPHA)
            {"\n"}
            Zazay Community, Paynesville City, Monrovia, Liberia
          </Text>

          <Text style={styles.footerRight}>
            OFFICIAL STUDENT RECORD • CONFIDENTIAL
            {"\n"}
            Powered by Polu Integrated School Management System
          </Text>
        </View>
      </Page>
    </Document>
  );
};

/* ============================================================
   AUTOMATIC PDF DOWNLOAD
============================================================ */

const Secure = ({ student }) => {
  useEffect(() => {
    if (!student) return undefined;

    let cancelled = false;
    let generatedUrl = null;

    const generatePDF = async () => {
      try {
        const blob = await pdf(
          <StudentAuthenticationPDF student={student} />
        ).toBlob();

        if (cancelled) return;

        generatedUrl = URL.createObjectURL(blob);

        const safeStudentId = String(
          student?.student_id ||
            student?.admission_id ||
            "Student"
        ).replace(/[^a-zA-Z0-9_-]/g, "_");

        const fileName =
          `RPHA_Student_Authentication_${safeStudentId}.pdf`;

        const link = document.createElement("a");
        link.href = generatedUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Allow the browser time to begin reading the generated PDF.
        setTimeout(() => {
          URL.revokeObjectURL(generatedUrl);
          generatedUrl = null;
        }, 1000);
      } catch (error) {
        console.error("Student PDF Generation Error:", error);
      }
    };

    generatePDF();

    return () => {
      cancelled = true;

      if (generatedUrl) {
        URL.revokeObjectURL(generatedUrl);
        generatedUrl = null;
      }
    };
  }, [student]);

  return null;
};

export default Secure;
